import { ChangeDetectionStrategy, Component, Input, OnDestroy, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';
import { AdminOnboardingService } from '../../../../services/admin-onboarding.service';

/**
 * Shared onboarding panel — the single UI + state machine for the "Setup/On-board
 * Consumer Portal" flow. Rendered by both the not-onboarded error overlay
 * (CustomerPortalAdminComponent) and the Setup tab (AdminMandatoryComponent);
 * each host only decides WHEN to show it and passes the org context.
 */
@Component({
    standalone: false,
    selector: 'por-cp-onboard-panel',
    templateUrl: './onboarding-panel.component.html',
    styleUrls: ['./onboarding-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OnboardingPanelComponent implements OnDestroy {
    private readonly onboardingService = inject(AdminOnboardingService);

    @Input({ required: true }) orgId = '';
    @Input() email = '';
    @Input() isProduction = false;
    /** Credentials carried by the GAdmin embed config, if any (resolution falls back to localStorage). */
    @Input() configCredentials: { cognitoToken?: string; relationshipId?: string } = {};

    onboardingState = signal<'idle' | 'loading' | 'success' | 'error'>('idle');
    showCredentialPrompt = signal(false);
    promptCognitoToken = signal('');
    promptRelationshipId = signal('');
    promptNeedsCognitoToken = signal(false);
    promptNeedsRelationshipId = signal(false);
    successMessage = signal('');
    errorMessage = signal('');

    private readonly subscriptions: Subscription[] = [];

    onOnboardClick(): void {
        const creds = this.onboardingService.resolveCredentials(this.configCredentials);

        // Both credentials available (real GAdmin scenario) — onboard directly.
        if (!creds.needsCognitoToken && !creds.needsRelationshipId) {
            this.executeOnboarding(creds.cognitoToken, creds.relationshipId);
            return;
        }

        // Otherwise prompt for the missing ones, pre-filling what we have.
        this.promptNeedsCognitoToken.set(creds.needsCognitoToken);
        this.promptNeedsRelationshipId.set(creds.needsRelationshipId);
        if (creds.cognitoToken) {
            this.promptCognitoToken.set(creds.cognitoToken);
        }
        if (creds.relationshipId) {
            this.promptRelationshipId.set(creds.relationshipId);
        }
        this.showCredentialPrompt.set(true);
    }

    onCredentialPromptSubmit(): void {
        const creds = this.onboardingService.resolveCredentials(this.configCredentials);
        const cognitoToken = this.promptNeedsCognitoToken() ? this.promptCognitoToken() : creds.cognitoToken;
        const relationshipId = this.promptNeedsRelationshipId() ? this.promptRelationshipId() : creds.relationshipId;

        if ((!cognitoToken && this.promptNeedsCognitoToken()) || (!relationshipId && this.promptNeedsRelationshipId())) {
            this.errorMessage.set('Please fill in all required fields.');
            this.onboardingState.set('error');
            return;
        }

        if (relationshipId && this.promptNeedsRelationshipId()) {
            this.onboardingService.persistRelationshipId(relationshipId);
        }

        this.executeOnboarding(cognitoToken || '', relationshipId || '');
    }

    onCredentialPromptCancel(): void {
        this.showCredentialPrompt.set(false);
        this.promptCognitoToken.set('');
        this.promptRelationshipId.set('');
        this.onboardingState.set('idle');
    }

    executeOnboarding(cognitoToken: string, relationshipId: string): void {
        if (this.isProduction && !confirm('WARNING: You are about to onboard on a PRODUCTION environment. Continue?')) {
            return;
        }

        this.onboardingState.set('loading');
        this.errorMessage.set('');
        this.successMessage.set('');

        this.subscriptions.push(
            this.onboardingService.onboardOrganization(this.email, this.orgId, relationshipId, cognitoToken).subscribe({
                next: response => {
                    this.onboardingState.set('success');
                    this.successMessage.set(this.onboardingService.buildSuccessMessage(this.orgId, response.vanityUrl));
                    this.showCredentialPrompt.set(false);
                },
                error: (error: HttpErrorResponse) => {
                    this.onboardingState.set('error');
                    this.errorMessage.set(this.onboardingService.mapErrorToUserMessage(error));
                }
            })
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
